import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { BLOG_POSTS } from "@/lib/content";
import { useParams, Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find(p => p.slug === slug);

  if (!post) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-h1 mb-4">Article Not Found</h1>
          <p className="text-body-lg text-slate-500 mb-8">The article you are looking for does not exist.</p>
          <Link href="/resources/blog">
            <Button variant="outline">Back to blog</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead title={`${post.title} | ClauseArk Blog`} description={post.excerpt} />
      
      <article className="pt-24 pb-32">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <Link href="/resources/blog" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to blog
          </Link>

          <header className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                {post.tag}
              </span>
              <span className="text-sm text-muted-foreground">•</span>
              <span className="text-sm text-muted-foreground">{post.date}</span>
              <span className="text-sm text-muted-foreground">•</span>
              <span className="text-sm text-muted-foreground">{post.readMin} min read</span>
            </div>
            <h1 className="text-display mb-8 text-navy dark:text-white">
              {post.title}
            </h1>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-sm font-bold text-slate-600 dark:text-slate-300">
                {post.author.charAt(0)}
              </div>
              <div>
                <div className="font-medium text-foreground">{post.author}</div>
              </div>
            </div>
          </header>

          <div className="prose prose-slate dark:prose-invert max-w-none prose-lg">
            <p className="lead text-xl text-slate-600 dark:text-slate-300 font-medium mb-12">
              {post.excerpt}
            </p>
            
            <div className="bg-slate-50 dark:bg-navy/30 border rounded-2xl p-12 text-center my-12">
              <h3 className="text-xl font-bold mb-4 text-navy dark:text-white">Full article coming soon</h3>
              <p className="text-muted-foreground mb-6">We're finalizing the details of this article. Check back later to read the full post.</p>
              <Link href="/company/contact">
                <Button variant="outline">Subscribe for updates</Button>
              </Link>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}
